// types

export type JsonPrimitive = boolean | number | string | null;
export type Primitive = JsonPrimitive | bigint | symbol | undefined;

export type JsonObject = {[Key in string]: JsonValue};
export type JsonArray = JsonValue[];
export type JsonValue = JsonArray | JsonObject | JsonPrimitive;

export type JsonableObject = {[Key in string]?: JsonableValue};
export type JsonableArray = JsonableValue[];
export type JsonableValue = JsonableArray | JsonableObject | Primitive;

export type Flat<T> = {[Key in keyof T]: T[Key]} & {};

// types

export const isNull = (x: any): x is null =>
  x === null;

export const isNone = (x: any): x is undefined =>
  typeof x === 'undefined';

export const isReal = <T>(x: T | null | undefined): x is T =>
  !isNull(x) && !isNone(x);

export const isStr = (x: any): x is string =>
  typeof x === 'string';

export const isNum = (x: any): x is number =>
  typeof x === 'number';

export const isBool = (x: any): x is boolean =>
  typeof x === 'boolean';

// eslint-disable-next-line @typescript-eslint/ban-types
export const isFunc = (x: any): x is Function =>
  typeof x === 'function';

// language

export const possessive = (noun: string) => (
  noun.slice(-1).toLowerCase() === 's' ? `${noun}'` : `${noun}'s`
);

// utils

export const uniq = <T>(arr: T[]) => [...new Set(arr)];
export const compact = <T>(arr: Array<T | null | undefined>) =>
  arr.filter(isReal);

export const ensureArray = <T>(x: T | T[]): T[] => (
  Array.isArray(x) ? x : [x]
);

export const times = <T>(fn: (n: number) => T, n: number): T[] =>
  Array.from({length: n}, (_, i) => fn(i));

export const splitEvery = <T>(n: number, arr: T[]): T[][] => {
  const out: T[][] = [];

  for (let i = 0; i < arr.length; i += n) {
    out.push(arr.slice(i, i + n));
  }
  
  return out;
}

export const splitStrEvery = (n: number, str: string) => (
  splitEvery(n, str.split('')).map((arr) => arr.join(''))
);

export const clamp = (min: number, max: number, val: number) =>
  Math.min(max, Math.max(min, val));

export const isEmpty = (obj: UnknownObj) =>
  Object.keys(obj).length === 0;

export type UnknownObj = Record<string, unknown>;

export const filterObj = <T extends UnknownObj>(
  predicate: (value: T[keyof T], key: keyof T) => boolean,
  obj: T,
): Partial<T> => {
  const newObj = {} as Partial<T>;

  for (const key in obj) {
    if (obj.hasOwnProperty(key) && predicate(obj[key], key)) {
      newObj[key] = obj[key];
    }
  }

  return newObj;
};

export const mapObj = <T extends UnknownObj, U>(
  mappingFn: (value: T[keyof T], key: keyof T) => U,
  obj: T,
): Record<keyof T, U> => {
  const newObj = {} as Record<keyof T, U>;

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      newObj[key] = mappingFn(obj[key], key);
    }
  }

  return newObj;
};

export const objToArr = <T extends UnknownObj, U>(
  obj: T, 
  fn: (v: T[keyof T], k: keyof T) => U,
): U[] => {
  const arr: U[] = [];

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      arr.push(fn(obj[key], key));
    }
  }

  return arr;
};

export const keysOf = <T extends UnknownObj>(obj: T) => {
  return Object.keys(obj) as Array<keyof T>;
};

export const manyKeysOf = <T extends UnknownObj>(obj: T) => {
  type K = keyof T;
  const keys = keysOf(obj);

  if (keys.length === 0) {
    throw new Error('Object has no keys');
  }
  
  return keys as [K, ...K[]];
};

export const isAbsoluteUrl = (url?: string | URL | null): boolean => {
  try {
    return isReal(url) && !!new URL(url);
  } catch (e) {
    return false;
  }
};

// tagged template literals

export function noop(strs: TemplateStringsArray, ...keys: string[]) {
  return strs.reduce((acc, str, i) => acc + str + (keys[i] || ''), '');
}

// quick id

export const getQuickId = (l = 12) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < l; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
};
